import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { useSecretsApi, SecretMetadata } from '@/api/useSecretsApi';
import { useQuery } from '@tanstack/react-query';

interface NodeInterfaceProps {
    integration: {
        callIntegration: <T>(params: { url: string; method: string; params?: any }) => Promise<{ data: T }>;
    };
    nodeShape: {
        nodeInPorts: Array<{ name: string }>;
        nodeOutPorts: Array<{ name: string }>;
    };
    updateShape: (shape: { inPorts: Array<{ name: string }>; outPorts: Array<{ name: string }> }) => void;
    nodeState: Record<string, any>;
    setNodeState: (key: string, value: any) => void;
    graphId?: string;
}

export const DynamicSecretSelector: React.FC<NodeInterfaceProps> = props => {
    const selectedSecrets: string[] = props.nodeState.availableSecrets || [];
    const api = useSecretsApi();

    const { data: availableSecrets = [] } = useQuery({
        queryKey: ['availableSecrets', props.graphId],
        queryFn: () => api.getAvailableSecrets(props.graphId!),
        enabled: !!props.graphId,
    });

    const toggleSecret = (name: string) => {
        const next = selectedSecrets.includes(name) ? selectedSecrets.filter(n => n !== name) : [...selectedSecrets, name];
        props.setNodeState('availableSecrets', next);
    };

    if (availableSecrets.length === 0) {
        return (
            <div className="px-2 py-1">
                <p className="text-[11px] text-neutral-400">No secrets available. Add secrets from the Secrets panel.</p>
            </div>
        );
    }

    return (
        <div className="px-2 py-1 flex flex-col gap-1">
            <span className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider mb-0.5">Available Secrets</span>
            {availableSecrets.map(secret => (
                <label
                    key={secret.id}
                    className="flex items-center gap-2 py-0.5 cursor-pointer text-[11px] text-neutral-700 dark:text-neutral-300"
                >
                    <Checkbox checked={selectedSecrets.includes(secret.name)} onCheckedChange={() => toggleSecret(secret.name)} />
                    <span>{secret.name}</span>
                    {secret.service && <span className="text-[9px] text-neutral-400">({secret.service})</span>}
                </label>
            ))}
        </div>
    );
};
